import React, { useEffect, useState } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './BoardList.css';

interface Board {
  id: number;
  user_id: number;
  category: string;
  title: string;
  content?: string;
  created_at: string;
  is_pinned?: boolean;
  comment_count?: number;
  thumbnail_url?: string | null;
  user: {
    username: string;
    display_name: string;
  };
}

const BoardList: React.FC<{ category: string }> = ({ category }) => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  const getThumbnailSrc = (thumbnailUrl: string) => (
    /^https?:\/\//i.test(thumbnailUrl) ? thumbnailUrl : `${apiBaseUrl}${thumbnailUrl}`
  );

  const getPlainText = (value?: string) => (
    (value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );

  const openBoard = (boardId: number) => {
    navigate(`/boards/${category}/${boardId}`);
  };

  const sortBoards = (nextBoards: Board[]) => (
    [...nextBoards].sort((a, b) => {
      const pinnedDiff = Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned));
      if (pinnedDiff !== 0) return pinnedDiff;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    })
  );

  useEffect(() => {
    const fetchBoards = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get(`/api/boards?category=${category}`);
        setBoards(sortBoards(response.data));
      } catch (err) {
        console.error('게시판 목록을 불러오는데 실패했습니다:', err);
        setError('게시판 목록을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchBoards();
  }, [category]);

  const handlePinToggle = async (board: Board) => {
    const nextPinned = !board.is_pinned;

    try {
      await axiosInstance.put(`/api/boards/${board.id}`, { is_pinned: nextPinned });
      setBoards(boards => sortBoards(
        boards.map(b => b.id === board.id ? { ...b, is_pinned: nextPinned } : b)
      ));
    } catch (err) {
      console.error('게시글 공지 고정 상태 변경에 실패했습니다:', err);
      alert('게시글 공지 고정 상태 변경에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: 48, textAlign: 'center', color: '#6b7280' }}>
        로딩 중...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: 48, textAlign: 'center', color: '#ef4444' }}>
        {error}
      </div>
    );
  }

  return (
    <div className="board-list-container">
      <div className="board-list-header">
        <h2 className="board-list-title">
          {category === 'notice' ? '📢 공지사항' : category === 'free' ? '💬 자유게시판' : '🔥 토론게시판'}
        </h2>
        {((category === 'notice' && isAdmin()) || category !== 'notice') && (
          <Link to={`/boards/${category}/create`} className="write-btn">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            글쓰기
          </Link>
        )}
      </div>

      <ul className="board-cards-grid">
        {boards.map(board => (
          <li 
            key={board.id} 
            className={`board-card ${board.is_pinned ? 'pinned' : ''} ${board.thumbnail_url ? 'has-thumbnail' : 'no-thumbnail'} ${isAdmin() ? 'has-admin-action' : ''}`}
            role="link"
            tabIndex={0}
            onClick={() => openBoard(board.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openBoard(board.id);
              }
            }}
          >
            {isAdmin() && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handlePinToggle(board);
                }} 
                className={`pin-action-btn ${board.is_pinned ? 'is-pinned' : ''}`}
                title={board.is_pinned ? '공지 고정 해제' : '공지로 지정'}
                aria-label={board.is_pinned ? '공지 고정 해제' : '공지로 지정'}
              >
                {board.is_pinned ? (
                  <svg className="pin-action-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M14 4l6 6-3 1-4 4v4l-1 1-4-4-4 4-1-1 4-4-4-4 1-1h4l4-4 1-3z" />
                    <line x1="4" y1="4" x2="20" y2="20" />
                  </svg>
                ) : (
                  <svg className="pin-action-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M14 4l6 6-3 1-4 4v4l-1 1-4-4-4 4-1-1 4-4-4-4 1-1h4l4-4 1-3z" />
                  </svg>
                )}
              </button>
            )}
            
            {board.thumbnail_url && (<div className="card-thumbnail-wrapper">
              {board.thumbnail_url ? (
                <img 
                  src={getThumbnailSrc(board.thumbnail_url)} 
                  alt={board.title} 
                  className="card-thumbnail"
                  loading="lazy"
                />
              ) : (
                <div className="card-placeholder-icon">
                  <svg viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="card-placeholder-text">이미지 없음</span>
                </div>
              )}
            </div>)}

            <div className="card-content">
              <div className="card-title-link">
                {board.is_pinned && (
                  <span className="pinned-badge">공지</span>
                )}
                {board.title}
              </div>
              {board.thumbnail_url && (
                <p className="card-excerpt">{getPlainText(board.content)}</p>
              )}
              
              <div className="card-meta">
                <span className="card-comments">댓글 {board.comment_count || 0}</span>
                <span className="card-author">✍️ {board.user.display_name || board.user.username}</span>
                <span className="card-date">{new Date(board.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </li>
        ))}
        {boards.length === 0 && (
          <li className="empty-state">
            <div className="empty-state-icon">📭</div>
            <div>등록된 게시글이 없습니다. 첫 글을 작성해 보세요!</div>
          </li>
        )}
      </ul>
    </div>
  );
};
export default BoardList; 
